import React, { useState, useEffect, useCallback } from 'react';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';

interface EditableSection {
  id: string;
  variableType: string;
  description: string;
  selector: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  priority?: number; // Lower number = higher priority (appears on top)
  isOrderIndexed?: boolean; // Whether this variable supports multiple items
}

interface FoundElement {
  element: Element;
  section: EditableSection;
  orderIndex?: number;
  uniqueId: string; // Combines section.id with orderIndex if present
}

// Map sections to their corresponding variable types
const EDITABLE_SECTIONS: EditableSection[] = [
  // SEO Variables
  {
    id: 'seo-title',
    variableType: 'SEO_TITLE',
    description: 'SEO title',
    selector: '[data-variable="SEO_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'seo-description',
    variableType: 'SEO_DESCRIPTION',
    description: 'SEO description',
    selector: '[data-variable="SEO_DESCRIPTION"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'seo-keywords',
    variableType: 'SEO_KEYWORDS',
    description: 'SEO keywords',
    selector: '[data-variable="SEO_KEYWORDS"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },

  // Theme Colors
  {
    id: 'theme-primary-color',
    variableType: 'THEME_PRIMARY_COLOR',
    description: 'Primary color',
    selector: '[data-variable="THEME_PRIMARY_COLOR"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'theme-secondary-color',
    variableType: 'THEME_SECONDARY_COLOR',
    description: 'Secondary color',
    selector: '[data-variable="THEME_SECONDARY_COLOR"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'theme-background-color',
    variableType: 'THEME_BACKGROUND_COLOR',
    description: 'Background color',
    selector: '[data-variable="THEME_BACKGROUND_COLOR"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'theme-background-color-light',
    variableType: 'THEME_BACKGROUND_COLOR_LIGHT',
    description: 'Light background color',
    selector: '[data-variable="THEME_BACKGROUND_COLOR_LIGHT"]',
    position: 'top',
    priority: 1,
  },

  // Home Hero Section (lower priority for background)
  {
    id: 'hero-background',
    variableType: 'HOME_HERO_BACKGROUND_IMAGE',
    description: 'Hero background image',
    selector: '[data-variable="HOME_HERO_BACKGROUND_IMAGE"]',
    position: 'top',
    priority: 10,
  },
  {
    id: 'hero-title',
    variableType: 'HOME_HERO_TITLE',
    description: 'Main headline',
    selector: '[data-variable="HOME_HERO_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'hero-subtitle',
    variableType: 'HERO_SUBTITLE',
    description: 'Hero subtitle',
    selector: '[data-variable="HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'residential-hero-subtitle',
    variableType: 'RESIDENTIAL_HERO_SUBTITLE',
    description: 'Residential hero subtitle',
    selector: '[data-variable="RESIDENTIAL_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'commercial-hero-subtitle',
    variableType: 'COMMERCIAL_HERO_SUBTITLE',
    description: 'Commercial hero subtitle',
    selector: '[data-variable="COMMERCIAL_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'rolloff-hero-subtitle',
    variableType: 'ROLLOFF_HERO_SUBTITLE',
    description: 'Rolloff hero subtitle',
    selector: '[data-variable="ROLLOFF_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'portable-toilet-hero-subtitle',
    variableType: 'PORTABLE_TOILET_HERO_SUBTITLE',
    description: 'Portable toilet hero subtitle',
    selector: '[data-variable="PORTABLE_TOILET_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'hero-image',
    variableType: 'HOME_HERO_IMAGE',
    description: 'Hero image',
    selector: '[data-variable="HOME_HERO_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'residential-image',
    variableType: 'RESIDENTIAL_IMAGE',
    description: 'Residential service image',
    selector: '[data-variable="RESIDENTIAL_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'commercial-image',
    variableType: 'COMMERCIAL_IMAGE',
    description: 'Commercial service image',
    selector: '[data-variable="COMMERCIAL_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'rolloff-image',
    variableType: 'ROLLOFF_IMAGE',
    description: 'Rolloff service image',
    selector: '[data-variable="ROLLOFF_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'portable-toilet-image',
    variableType: 'PORTABLE_TOILET_IMAGE',
    description: 'Portable toilet service image',
    selector: '[data-variable="PORTABLE_TOILET_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'hero-feature',
    variableType: 'HOME_HERO_FEATURE',
    description: 'Hero feature',
    selector: '[data-variable="HOME_HERO_FEATURE"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },

  // About Section
  {
    id: 'about-hero-subtitle',
    variableType: 'ABOUT_HERO_SUBTITLE',
    description: 'About hero subtitle',
    selector: '[data-variable="ABOUT_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'about-title',
    variableType: 'ABOUT_TITLE',
    description: 'About section title',
    selector: '[data-variable="ABOUT_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'about-content',
    variableType: 'ABOUT_CONTENT',
    description: 'About section content',
    selector: '[data-variable="ABOUT_CONTENT"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'who-we-are-title',
    variableType: 'WHO_WE_ARE_TITLE',
    description: 'Who we are title',
    selector: '[data-variable="WHO_WE_ARE_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'about-image',
    variableType: 'ABOUT_US_IMAGE',
    description: 'About section image',
    selector: '[data-variable="ABOUT_US_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Contact Section
  {
    id: 'contact-us-hero-subtitle',
    variableType: 'CONTACT_US_HERO_SUBTITLE',
    description: 'Contact us hero subtitle',
    selector: '[data-variable="CONTACT_US_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'contact-title',
    variableType: 'CONTACT_TITLE',
    description: 'Contact section title',
    selector: '[data-variable="CONTACT_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'contact-subtitle',
    variableType: 'CONTACT_SUBTITLE',
    description: 'Contact section subtitle',
    selector: '[data-variable="CONTACT_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'contact-us-image',
    variableType: 'CONTACT_US_IMAGE',
    description: 'Contact us image',
    selector: '[data-variable="CONTACT_US_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Logo and Navigation
  {
    id: 'logo',
    variableType: 'LOGO_IMAGE',
    description: 'Company logo',
    selector: '[data-variable="LOGO_IMAGE"]',
    position: 'bottom',
    priority: 1,
  },

  // Call to Action
  {
    id: 'cta-text',
    variableType: 'CTA_TEXT',
    description: 'Call-to-action text',
    selector: '[data-variable="CTA_TEXT"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'cta-button',
    variableType: 'CTA_BUTTON_TEXT',
    description: 'Call-to-action button text',
    selector: '[data-variable="CTA_BUTTON_TEXT"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'about-company-button',
    variableType: 'ABOUT_COMPANY_BUTTON_TEXT',
    description: 'About our company button text',
    selector: '[data-variable="ABOUT_COMPANY_BUTTON_TEXT"]',
    position: 'top',
    priority: 1,
  },

  // Footer
  {
    id: 'footer-text',
    variableType: 'FOOTER_TEXT',
    description: 'Footer text',
    selector: '[data-variable="FOOTER_TEXT"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'facebook-link',
    variableType: 'FACEBOOK_LINK',
    description: 'Facebook Page',
    selector: '[data-variable="FACEBOOK_LINK"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'x-link',
    variableType: 'X_LINK',
    description: 'X (Twitter)',
    selector: '[data-variable="X_LINK"]',
    position: 'top',
    priority: 1,
  },

  // Why Choose Us Section
  {
    id: 'why-choose-us-section-title',
    variableType: 'WHY_CHOOSE_US_SECTION_TITLE',
    description: 'Why choose us section title',
    selector: '[data-variable="WHY_CHOOSE_US_SECTION_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'why-choose-us-title',
    variableType: 'WHY_CHOOSE_US_TITLE',
    description: 'Why choose us title',
    selector: '[data-variable="WHY_CHOOSE_US_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'why-choose-us-subtitle',
    variableType: 'WHY_CHOOSE_US_SUBTITLE',
    description: 'Why choose us subtitle',
    selector: '[data-variable="WHY_CHOOSE_US_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'why-choose-us-feature',
    variableType: 'WHY_CHOOSE_US_FEATURE',
    description: 'Why choose us feature',
    selector: '[data-variable="WHY_CHOOSE_US_FEATURE"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'why-choose-us-image',
    variableType: 'WHY_CHOOSE_US_IMAGE',
    description: 'Why choose us image',
    selector: '[data-variable="WHY_CHOOSE_US_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Learn More Section
  {
    id: 'learn-more-section-title',
    variableType: 'LEARN_MORE_SECTION_TITLE',
    description: 'Learn more section title',
    selector: '[data-variable="LEARN_MORE_SECTION_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'learn-more-title',
    variableType: 'LEARN_MORE_TITLE',
    description: 'Learn more title',
    selector: '[data-variable="LEARN_MORE_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'learn-more-subtitle',
    variableType: 'LEARN_MORE_SUBTITLE',
    description: 'Learn more subtitle',
    selector: '[data-variable="LEARN_MORE_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'learn-more-feature',
    variableType: 'LEARN_MORE_FEATURE',
    description: 'Learn more feature',
    selector: '[data-variable="LEARN_MORE_FEATURE"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'learn-more-image',
    variableType: 'LEARN_MORE_IMAGE',
    description: 'Learn more image',
    selector: '[data-variable="LEARN_MORE_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Company Stats
  {
    id: 'stats-title',
    variableType: 'STATS_TITLE',
    description: 'Statistic title',
    selector: '[data-variable="STATS_TITLE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'stats-subtitle',
    variableType: 'STATS_SUBTITLE',
    description: 'Statistic description',
    selector: '[data-variable="STATS_SUBTITLE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'stats-value',
    variableType: 'STATS_VALUE',
    description: 'Statistic value',
    selector: '[data-variable="STATS_VALUE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },

  // Company Stats Section
  {
    id: 'company-stats-title',
    variableType: 'COMPANY_STATS_TITLE',
    description: 'Company stats section title',
    selector: '[data-variable="COMPANY_STATS_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'company-stats-subtitle',
    variableType: 'COMPANY_STATS_SUBTITLE',
    description: 'Company stats section subtitle',
    selector: '[data-variable="COMPANY_STATS_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },

  // Service Areas
  {
    id: 'service-area-hero-subtitle',
    variableType: 'SERVICE_AREA_HERO_SUBTITLE',
    description: 'Service area hero subtitle',
    selector: '[data-variable="SERVICE_AREA_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'service-area-title',
    variableType: 'SERVICE_AREA_TITLE',
    description: 'Service area title',
    selector: '[data-variable="SERVICE_AREA_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'service-area-subtitle',
    variableType: 'SERVICE_AREA_SUBTITLE',
    description: 'Service area subtitle',
    selector: '[data-variable="SERVICE_AREA_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'service-area-section-title',
    variableType: 'SERVICE_AREA_SECTION_TITLE',
    description: 'Service area section title',
    selector: '[data-variable="SERVICE_AREA_SECTION_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'service-area-name',
    variableType: 'SERVICE_AREA_NAME',
    description: 'Area',
    selector: '[data-variable="SERVICE_AREA_NAME"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'service-area-map',
    variableType: 'SERVICE_AREA_MAP_IMAGE',
    description: 'Service area map',
    selector: '[data-variable="SERVICE_AREA_MAP_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Service Types Labels and Descriptions
  {
    id: 'residential-service-label',
    variableType: 'RESIDENTIAL_SERVICE_LABEL',
    description: 'Residential service label',
    selector: '[data-variable="RESIDENTIAL_SERVICE_LABEL"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'residential-service-description',
    variableType: 'RESIDENTIAL_SERVICE_DESCRIPTION',
    description: 'Residential service description',
    selector: '[data-variable="RESIDENTIAL_SERVICE_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'commercial-service-label',
    variableType: 'COMMERCIAL_SERVICE_LABEL',
    description: 'Commercial service label',
    selector: '[data-variable="COMMERCIAL_SERVICE_LABEL"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'commercial-service-description',
    variableType: 'COMMERCIAL_SERVICE_DESCRIPTION',
    description: 'Commercial service description',
    selector: '[data-variable="COMMERCIAL_SERVICE_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'rolloff-service-label',
    variableType: 'ROLLOFF_SERVICE_LABEL',
    description: 'Rolloff service label',
    selector: '[data-variable="ROLLOFF_SERVICE_LABEL"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'rolloff-service-description',
    variableType: 'ROLLOFF_SERVICE_DESCRIPTION',
    description: 'Rolloff service description',
    selector: '[data-variable="ROLLOFF_SERVICE_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'portable-toilet-service-label',
    variableType: 'PORTABLE_TOILET_SERVICE_LABEL',
    description: 'Portable toilet service label',
    selector: '[data-variable="PORTABLE_TOILET_SERVICE_LABEL"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'portable-toilet-service-description',
    variableType: 'PORTABLE_TOILET_SERVICE_DESCRIPTION',
    description: 'Portable toilet service description',
    selector: '[data-variable="PORTABLE_TOILET_SERVICE_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },

  // Community Service
  {
    id: 'community-service-title',
    variableType: 'COMMUNITY_SERVICE_TITLE',
    description: 'Community service title',
    selector: '[data-variable="COMMUNITY_SERVICE_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'community-service-subtitle',
    variableType: 'COMMUNITY_SERVICE_SUBTITLE',
    description: 'Community service subtitle',
    selector: '[data-variable="COMMUNITY_SERVICE_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'community-service-section-title',
    variableType: 'COMMUNITY_SERVICE_SECTION_TITLE',
    description: 'Community service section title',
    selector: '[data-variable="COMMUNITY_SERVICE_SECTION_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'community-service-image',
    variableType: 'COMMUNITY_SERVICE_IMAGE',
    description: 'Community service image',
    selector: '[data-variable="COMMUNITY_SERVICE_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Materials
  {
    id: 'allowed-material-name',
    variableType: 'ALLOWED_MATERIAL_NAME',
    description: 'Allowed material name',
    selector: '[data-variable="ALLOWED_MATERIAL_NAME"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'allowed-material-description',
    variableType: 'ALLOWED_MATERIAL_DESCRIPTION',
    description: 'Allowed material description',
    selector: '[data-variable="ALLOWED_MATERIAL_DESCRIPTION"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'not-allowed-material-name',
    variableType: 'NOT_ALLOWED_MATERIAL_NAME',
    description: 'Not allowed material name',
    selector: '[data-variable="NOT_ALLOWED_MATERIAL_NAME"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'not-allowed-material-description',
    variableType: 'NOT_ALLOWED_MATERIAL_DESCRIPTION',
    description: 'Not allowed material description',
    selector: '[data-variable="NOT_ALLOWED_MATERIAL_DESCRIPTION"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },

  // Heavy Debris
  {
    id: 'heavy-debris-title',
    variableType: 'HEAVY_DEBRIS_TITLE',
    description: 'Heavy debris title',
    selector: '[data-variable="HEAVY_DEBRIS_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'heavy-debris-description',
    variableType: 'HEAVY_DEBRIS_DESCRIPTION',
    description: 'Heavy debris description',
    selector: '[data-variable="HEAVY_DEBRIS_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'heavy-debris-image',
    variableType: 'HEAVY_DEBRIS_IMAGE',
    description: 'Heavy debris image',
    selector: '[data-variable="HEAVY_DEBRIS_IMAGE"]',
    position: 'top',
    priority: 1,
  },

  // Rental Media
  {
    id: 'rental-media-title',
    variableType: 'RENTAL_MEDIA_TITLE',
    description: 'Rental media title',
    selector: '[data-variable="RENTAL_MEDIA_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'rental-media-subtitle',
    variableType: 'RENTAL_MEDIA_SUBTITLE',
    description: 'Rental media subtitle',
    selector: '[data-variable="RENTAL_MEDIA_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'rental-media-url',
    variableType: 'RENTAL_MEDIA_URL',
    description: 'Rental media content (image or video)',
    selector: '[data-variable="RENTAL_MEDIA_URL"]',
    position: 'top',
    priority: 1,
  },

  // How-to Steps
  {
    id: 'how-to-step-title',
    variableType: 'HOW_TO_STEP_TITLE',
    description: 'How-to step title',
    selector: '[data-variable="HOW_TO_STEP_TITLE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'how-to-step-subtitle',
    variableType: 'HOW_TO_STEP_SUBTITLE',
    description: 'How-to step subtitle',
    selector: '[data-variable="HOW_TO_STEP_SUBTITLE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'how-to-step-content',
    variableType: 'HOW_TO_STEP_CONTENT',
    description: 'How-to step content',
    selector: '[data-variable="HOW_TO_STEP_CONTENT"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'how-to-step-icon',
    variableType: 'HOW_TO_STEP_ICON',
    description: 'How-to step icon',
    selector: '[data-variable="HOW_TO_STEP_ICON"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'how-to-rental-subtitle',
    variableType: 'HOW_TO_RENTAL_SUBTITLE',
    description: 'Rental process subtitle',
    selector: '[data-variable="HOW_TO_RENTAL_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'how-to-rental-title',
    variableType: 'HOW_TO_RENTAL_TITLE',
    description: 'Rental process title',
    selector: '[data-variable="HOW_TO_RENTAL_TITLE"]',
    position: 'top',
    priority: 1,
  },

  // Material Guide
  {
    id: 'material-guide-title',
    variableType: 'MATERIAL_GUIDE_TITLE',
    description: 'Material guide title',
    selector: '[data-variable="MATERIAL_GUIDE_TITLE"]',
    position: 'top',
    priority: 1,
  },

  // Skills
  {
    id: 'skill-section-subtitle',
    variableType: 'SKILL_SECTION_SUBTITLE',
    description: 'Skills section subtitle',
    selector: '[data-variable="SKILL_SECTION_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'skill-section-title',
    variableType: 'SKILL_SECTION_TITLE',
    description: 'Skills section title',
    selector: '[data-variable="SKILL_SECTION_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'skill-section-description',
    variableType: 'SKILL_SECTION_DESCRIPTION',
    description: 'Skills section description',
    selector: '[data-variable="SKILL_SECTION_DESCRIPTION"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'skill-title',
    variableType: 'SKILL_TITLE',
    description: 'Skill title',
    selector: '[data-variable="SKILL_TITLE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'skill-subtitle',
    variableType: 'SKILL_SUBTITLE',
    description: 'Skill subtitle',
    selector: '[data-variable="SKILL_SUBTITLE"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'skill-content',
    variableType: 'SKILL_CONTENT',
    description: 'Skill content',
    selector: '[data-variable="SKILL_CONTENT"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'skill-icon',
    variableType: 'SKILL_ICON',
    description: 'Skill icon',
    selector: '[data-variable="SKILL_ICON"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },

  // FAQ Variables
  {
    id: 'home-faq-question',
    variableType: 'HOME_FAQ_QUESTION',
    description: 'Home FAQ question',
    selector: '[data-variable="HOME_FAQ_QUESTION"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'home-faq-answer',
    variableType: 'HOME_FAQ_ANSWER',
    description: 'Home FAQ answer',
    selector: '[data-variable="HOME_FAQ_ANSWER"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'how-to-faq-question',
    variableType: 'HOW_TO_FAQ_QUESTION',
    description: 'How-to FAQ question',
    selector: '[data-variable="HOW_TO_FAQ_QUESTION"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'how-to-faq-answer',
    variableType: 'HOW_TO_FAQ_ANSWER',
    description: 'How-to FAQ answer',
    selector: '[data-variable="HOW_TO_FAQ_ANSWER"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'residential-faq-question',
    variableType: 'RESIDENTIAL_FAQ_QUESTION',
    description: 'Residential FAQ question',
    selector: '[data-variable="RESIDENTIAL_FAQ_QUESTION"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'residential-faq-answer',
    variableType: 'RESIDENTIAL_FAQ_ANSWER',
    description: 'Residential FAQ answer',
    selector: '[data-variable="RESIDENTIAL_FAQ_ANSWER"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'commercial-faq-question',
    variableType: 'COMMERCIAL_FAQ_QUESTION',
    description: 'Commercial FAQ question',
    selector: '[data-variable="COMMERCIAL_FAQ_QUESTION"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'commercial-faq-answer',
    variableType: 'COMMERCIAL_FAQ_ANSWER',
    description: 'Commercial FAQ answer',
    selector: '[data-variable="COMMERCIAL_FAQ_ANSWER"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'rolloff-faq-question',
    variableType: 'ROLLOFF_FAQ_QUESTION',
    description: 'Rolloff FAQ question',
    selector: '[data-variable="ROLLOFF_FAQ_QUESTION"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'rolloff-faq-answer',
    variableType: 'ROLLOFF_FAQ_ANSWER',
    description: 'Rolloff FAQ answer',
    selector: '[data-variable="ROLLOFF_FAQ_ANSWER"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'portable-toilet-faq-question',
    variableType: 'PORTABLE_TOILET_FAQ_QUESTION',
    description: 'Portable toilet FAQ question',
    selector: '[data-variable="PORTABLE_TOILET_FAQ_QUESTION"]',
    position: 'top',
    priority: 1,
    isOrderIndexed: true,
  },
  {
    id: 'portable-toilet-faq-answer',
    variableType: 'PORTABLE_TOILET_FAQ_ANSWER',
    description: 'Portable toilet FAQ answer',
    selector: '[data-variable="PORTABLE_TOILET_FAQ_ANSWER"]',
    position: 'right',
    priority: 1,
    isOrderIndexed: true,
  },

  // FAQ Section
  {
    id: 'faq-hero-subtitle',
    variableType: 'FAQ_HERO_SUBTITLE',
    description: 'FAQ hero subtitle',
    selector: '[data-variable="FAQ_HERO_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'faq-title',
    variableType: 'FAQ_TITLE',
    description: 'FAQ section title',
    selector: '[data-variable="FAQ_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'faq-subtitle',
    variableType: 'FAQ_SUBTITLE',
    description: 'FAQ section subtitle',
    selector: '[data-variable="FAQ_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'faq-description',
    variableType: 'FAQ_DESCRIPTION',
    description: 'FAQ section description',
    selector: '[data-variable="FAQ_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'faq-button',
    variableType: 'FAQ_BUTTON_TEXT',
    description: 'FAQ button text',
    selector: '[data-variable="FAQ_BUTTON_TEXT"]',
    position: 'top',
    priority: 1,
  },

  // Contact Form
  {
    id: 'contact-form-title',
    variableType: 'CONTACT_FORM_TITLE',
    description: 'Contact form title',
    selector: '[data-variable="CONTACT_FORM_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'contact-form-subtitle',
    variableType: 'CONTACT_FORM_SUBTITLE',
    description: 'Contact form subtitle',
    selector: '[data-variable="CONTACT_FORM_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'contact-form-description',
    variableType: 'CONTACT_FORM_DESCRIPTION',
    description: 'Contact form description',
    selector: '[data-variable="CONTACT_FORM_DESCRIPTION"]',
    position: 'right',
    priority: 1,
  },
  {
    id: 'form-submit-button',
    variableType: 'FORM_SUBMIT_BUTTON_TEXT',
    description: 'Form submit button text',
    selector: '[data-variable="FORM_SUBMIT_BUTTON_TEXT"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'contact-us-button',
    variableType: 'CONTACT_US_BUTTON_TEXT',
    description: 'Contact us button text',
    selector: '[data-variable="CONTACT_US_BUTTON_TEXT"]',
    position: 'top',
    priority: 1,
  },

  // Dumpster Types Section
  {
    id: 'dumpster-types-subtitle',
    variableType: 'DUMPSTER_TYPES_SUBTITLE',
    description: 'Dumpster types subtitle',
    selector: '[data-variable="DUMPSTER_TYPES_SUBTITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'dumpster-map-rates-title',
    variableType: 'DUMPSTER_MAP_RATES_TITLE',
    description: 'Dumpster map rates title',
    selector: '[data-variable="DUMPSTER_MAP_RATES_TITLE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'favicon-image',
    variableType: 'FAVICON_IMAGE',
    description: 'Favicon image',
    selector: '[data-variable="FAVICON_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'residential-map-rates-image',
    variableType: 'RESIDENTIAL_MAP_RATES_IMAGE',
    description: 'Residential map rates image',
    selector: '[data-variable="RESIDENTIAL_MAP_RATES_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'commercial-map-rates-image',
    variableType: 'COMMERCIAL_MAP_RATES_IMAGE',
    description: 'Commercial map rates image',
    selector: '[data-variable="COMMERCIAL_MAP_RATES_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'rolloff-map-rates-image',
    variableType: 'ROLLOFF_MAP_RATES_IMAGE',
    description: 'Rolloff map rates image',
    selector: '[data-variable="ROLLOFF_MAP_RATES_IMAGE"]',
    position: 'top',
    priority: 1,
  },
  {
    id: 'portable-toilet-map-rates-image',
    variableType: 'PORTABLE_TOILET_MAP_RATES_IMAGE',
    description: 'Portable toilet map rates image',
    selector: '[data-variable="PORTABLE_TOILET_MAP_RATES_IMAGE"]',
    position: 'top',
    priority: 1,
  },
];

export const EditOverlay: React.FC = () => {
  const { isEditMode } = useTenantContext();
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [foundElements, setFoundElements] = useState<FoundElement[]>([]);
  const [elementPositions, setElementPositions] = useState<
    Map<string, DOMRect>
  >(new Map());

  // Bulk selection state
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectedElements, setSelectedElements] = useState<Set<string>>(
    new Set(),
  );

  const updateElementPositions = useCallback(() => {
    if (!isEditMode || foundElements.length === 0) return;

    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      const newPositions = new Map<string, DOMRect>();
      foundElements.forEach((foundElement) => {
        const rect = foundElement.element.getBoundingClientRect();
        newPositions.set(foundElement.uniqueId, rect);
      });
      setElementPositions(newPositions);
    });
  }, [isEditMode, foundElements]);

  const findElements = useCallback(() => {
    const elements: FoundElement[] = [];

    EDITABLE_SECTIONS.forEach((section) => {
      const foundDOMElements = document.querySelectorAll(section.selector);

      foundDOMElements.forEach((element) => {
        const orderIndexAttr = element.getAttribute('data-order-index');
        const orderIndex = orderIndexAttr
          ? parseInt(orderIndexAttr, 10)
          : undefined;

        const uniqueId =
          section.isOrderIndexed && orderIndex !== undefined
            ? `${section.id}-${orderIndex}`
            : section.id;

        elements.push({
          element,
          section,
          orderIndex,
          uniqueId,
        });
      });
    });

    // Sort by priority (lower number = higher priority = appears on top)
    elements.sort(
      (a, b) => (a.section.priority || 999) - (b.section.priority || 999),
    );

    // Smart deduplication: for non-order-indexed elements with the same variable type,
    // keep the one that's most visible in the current viewport
    const deduplicatedElements: FoundElement[] = [];
    const variableTypeGroups = new Map<string, FoundElement[]>();

    // Group elements by variable type
    elements.forEach((element) => {
      if (element.section.isOrderIndexed) {
        // Keep all order-indexed elements as they represent different instances
        deduplicatedElements.push(element);
      } else {
        const variableType = element.section.variableType;
        if (!variableTypeGroups.has(variableType)) {
          variableTypeGroups.set(variableType, []);
        }
        variableTypeGroups.get(variableType)!.push(element);
      }
    });

    // For each variable type group, select the most visible element
    variableTypeGroups.forEach((elementsOfType) => {
      if (elementsOfType.length === 1) {
        // Only one element, add it
        deduplicatedElements.push(elementsOfType[0]);
      } else {
        // Multiple elements, find the most visible one in viewport
        let bestElement = elementsOfType[0];
        let bestVisibility = 0;

        elementsOfType.forEach((element) => {
          const rect = element.element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          // Calculate visibility score (0 to 1)
          let visibility = 0;
          if (rect.top < viewportHeight && rect.bottom > 0) {
            const visibleTop = Math.max(0, rect.top);
            const visibleBottom = Math.min(viewportHeight, rect.bottom);
            const visibleHeight = visibleBottom - visibleTop;
            visibility = visibleHeight / rect.height;
          }

          // Prefer elements that are more visible
          if (visibility > bestVisibility) {
            bestVisibility = visibility;
            bestElement = element;
          }
        });

        deduplicatedElements.push(bestElement);
      }
    });

    setFoundElements(deduplicatedElements);

    // Immediately update positions after finding elements
    requestAnimationFrame(() => {
      const newPositions = new Map<string, DOMRect>();
      deduplicatedElements.forEach((foundElement) => {
        const rect = foundElement.element.getBoundingClientRect();
        newPositions.set(foundElement.uniqueId, rect);
      });
      setElementPositions(newPositions);
    });
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      setFoundElements([]);
      setElementPositions(new Map());
      setSelectedElement(null);
      return;
    }

    findElements();

    // Force immediate position update when edit mode is enabled
    requestAnimationFrame(() => {
      updateElementPositions();
    });
  }, [isEditMode, findElements]);

  useEffect(() => {
    // Update positions immediately when foundElements change
    if (foundElements.length > 0) {
      updateElementPositions();
    }
  }, [foundElements, updateElementPositions]);

  // Listen for page navigation changes to refresh elements
  useEffect(() => {
    if (!isEditMode) return;

    const handlePopState = () => {
      // Immediate update on navigation
      setTimeout(() => {
        findElements();
      }, 50); // Reduced from longer delay
    };

    window.addEventListener('popstate', handlePopState);

    // Also listen for pushState/replaceState (for SPA navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      // Immediate update on navigation
      setTimeout(() => {
        findElements();
      }, 50); // Reduced delay
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      // Immediate update on navigation
      setTimeout(() => {
        findElements();
      }, 50); // Reduced delay
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [isEditMode, findElements]);

  useEffect(() => {
    if (!isEditMode) return;

    // Add scroll and resize listeners to update positions
    const handleScrollOrResize = () => {
      updateElementPositions();
    };

    // Use passive listeners for better performance
    window.addEventListener('scroll', handleScrollOrResize, {
      passive: true,
      capture: true, // Capture during capture phase for immediate response
    });
    window.addEventListener('resize', handleScrollOrResize, {
      passive: true,
      capture: true,
    });

    // Also listen for any DOM changes that might affect layout
    const observer = new MutationObserver(() => {
      // Reduced delay for faster response
      setTimeout(() => {
        findElements();
        updateElementPositions();
      }, 10); // Much faster than previous 10ms
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-variable'], // Added data-variable to watch for changes
    });

    // Additional event listeners for layout changes
    window.addEventListener('orientationchange', handleScrollOrResize);
    window.addEventListener('load', handleScrollOrResize);
    document.addEventListener('DOMContentLoaded', handleScrollOrResize);

    // Listen for CSS transitions and animations that might affect layout
    document.addEventListener('transitionend', handleScrollOrResize);
    document.addEventListener('animationend', handleScrollOrResize);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('orientationchange', handleScrollOrResize);
      window.removeEventListener('load', handleScrollOrResize);
      document.removeEventListener('DOMContentLoaded', handleScrollOrResize);
      document.removeEventListener('transitionend', handleScrollOrResize);
      document.removeEventListener('animationend', handleScrollOrResize);
      observer.disconnect();
    };
  }, [isEditMode, findElements, updateElementPositions]);

  // Bulk selection handlers
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isBulkSelectMode || e.button !== 0) return; // Only left mouse button

      // Check if the mousedown is on an editable element or its children
      const target = e.target as Element;
      const isOnEditableElement = target.closest('[data-editable-element]');

      // If clicking on an editable element, don't start drag selection
      if (isOnEditableElement) return;

      e.preventDefault();
      setIsSelecting(true);
      setSelectionStart({ x: e.clientX, y: e.clientY });
      setSelectionEnd({ x: e.clientX, y: e.clientY });
      // Only clear selection when starting a new drag selection, not on element clicks
      setSelectedElements(new Set());
    },
    [isBulkSelectMode],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isBulkSelectMode || !isSelecting || !selectionStart) return;

      e.preventDefault();
      setSelectionEnd({ x: e.clientX, y: e.clientY });

      // Calculate selection rectangle
      const rect = {
        left: Math.min(selectionStart.x, e.clientX),
        top: Math.min(selectionStart.y, e.clientY),
        right: Math.max(selectionStart.x, e.clientX),
        bottom: Math.max(selectionStart.y, e.clientY),
      };

      // Find intersecting elements
      const intersecting = new Set<string>();
      foundElements.forEach((foundElement) => {
        const elementRect = elementPositions.get(foundElement.uniqueId);
        if (!elementRect) return;

        // Calculate the actual overlay rectangle (same logic as in render)
        const overlayRect = (() => {
          if (foundElement.section.id === 'hero-background') {
            return {
              left: elementRect.left,
              top:
                elementRect.top +
                elementRect.height -
                Math.min(elementRect.height, 100),
              right: elementRect.left + Math.min(elementRect.width, 200),
              bottom: elementRect.top + elementRect.height,
            };
          } else if (
            foundElement.section.id === 'hero-image' ||
            foundElement.section.id === 'residential-image' ||
            foundElement.section.id === 'commercial-image' ||
            foundElement.section.id === 'rolloff-image' ||
            foundElement.section.id === 'portable-toilet-image'
          ) {
            return {
              left:
                elementRect.left +
                elementRect.width -
                Math.min(elementRect.width, 200),
              top:
                elementRect.top +
                elementRect.height -
                Math.min(elementRect.height, 150),
              right: elementRect.right,
              bottom: elementRect.bottom,
            };
          } else if (foundElement.section.id === 'facebook-link') {
            return {
              left: elementRect.left - 40,
              top: elementRect.top - 40,
              right: elementRect.right + 40,
              bottom: elementRect.bottom + 20,
            };
          } else if (foundElement.section.id === 'x-link') {
            return {
              left: elementRect.left - 40,
              top: elementRect.top + 40,
              right: elementRect.right + 40,
              bottom: elementRect.bottom + 20,
            };
          } else {
            return {
              left: elementRect.left,
              top: elementRect.top,
              right: elementRect.right,
              bottom: elementRect.bottom,
            };
          }
        })();

        // Check if selection rectangle intersects with the visual overlay rectangle
        if (
          rect.left < overlayRect.right &&
          rect.right > overlayRect.left &&
          rect.top < overlayRect.bottom &&
          rect.bottom > overlayRect.top
        ) {
          intersecting.add(foundElement.uniqueId);
        }
      });

      setSelectedElements(intersecting);
    },
    [
      isBulkSelectMode,
      isSelecting,
      selectionStart,
      foundElements,
      elementPositions,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (!isBulkSelectMode || !isSelecting) return;

    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, [isBulkSelectMode, isSelecting]);

  // Add/remove mouse event listeners for bulk selection
  useEffect(() => {
    if (!isBulkSelectMode) {
      setSelectedElements(new Set());
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isBulkSelectMode, handleMouseDown, handleMouseMove, handleMouseUp]);

  // Listen for bulk select mode changes from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'TOGGLE_BULK_SELECT') {
        setIsBulkSelectMode(event.data.enabled);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const exitBulkSelectMode = () => {
    setSelectedElements(new Set());
    setIsBulkSelectMode(false);
  };

  const handleElementClick = (
    foundElement: FoundElement,
    event?: React.MouseEvent,
  ) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // If in bulk select mode, toggle element selection (don't send individual messages)
    if (isBulkSelectMode) {
      const newSelected = new Set(selectedElements);
      if (newSelected.has(foundElement.uniqueId)) {
        newSelected.delete(foundElement.uniqueId);
      } else {
        newSelected.add(foundElement.uniqueId);
      }
      setSelectedElements(newSelected);
      return;
    }

    // Normal single selection behavior (only when NOT in bulk mode)
    if (selectedElement === foundElement.uniqueId) {
      setSelectedElement(null);

      // Send message to clear the prompt when unselecting
      window.parent.postMessage(
        {
          type: 'CLEAR_EDIT_PROMPT',
        },
        '*',
      );
      return;
    }

    setSelectedElement(foundElement.uniqueId);

    const isOrderIndexed = foundElement.section.isOrderIndexed;
    const orderIndex = foundElement.orderIndex;

    let message: string;
    if (isOrderIndexed && orderIndex !== undefined) {
      message = `Can you change the ${foundElement.section.description} #${
        orderIndex + 1
      } to instead be: `;
    } else {
      message = `Can you change the ${foundElement.section.description} to instead be: `;
    }

    window.parent.postMessage(
      {
        type: 'EDIT_VARIABLE',
        variableType: foundElement.section.variableType,
        description: foundElement.section.description,
        orderIndex: foundElement.orderIndex,
        message: message,
        replace: true,
      },
      '*',
    );
  };

  // Auto-send bulk selection messages when selection changes
  useEffect(() => {
    if (!isBulkSelectMode || selectedElements.size === 0) return;

    // Debounce to avoid sending too many messages during rapid selection
    const timeoutId = setTimeout(() => {
      const selectedElementsData = foundElements
        .filter((foundElement) => selectedElements.has(foundElement.uniqueId))
        .map((foundElement) => ({
          variableType: foundElement.section.variableType,
          description: foundElement.section.description,
          orderIndex: foundElement.orderIndex,
          uniqueId: foundElement.uniqueId,
        }));

      // Create a message describing all selected variables
      let message = `Can you help me update these ${selectedElementsData.length} variables:\n\n`;

      selectedElementsData.forEach((element, index) => {
        const displayText =
          element.orderIndex !== undefined
            ? `${element.description} #${element.orderIndex + 1}`
            : element.description;
        message += `${index + 1}. ${displayText}\n`;
      });

      // Send message to parent window
      window.parent.postMessage(
        {
          type: 'BULK_EDIT_VARIABLES',
          selectedVariables: selectedElementsData,
          message: message,
          replace: true,
        },
        '*',
      );
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [selectedElements, foundElements, isBulkSelectMode]);

  const handleRemove = (
    foundElement: FoundElement,
    event?: React.MouseEvent,
  ) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Set this remove button as selected
    setSelectedElement(`remove-${foundElement.uniqueId}`);

    const message = `Can you remove the ${foundElement.section.description} #${
      (foundElement.orderIndex || 0) + 1
    }`;

    // Send message to parent window
    window.parent.postMessage(
      {
        type: 'REMOVE_VARIABLE',
        variableType: foundElement.section.variableType,
        description: foundElement.section.description,
        orderIndex: foundElement.orderIndex,
        message: message,
        replace: true,
      },
      '*',
    );
  };

  const handleAddNew = (section: EditableSection, event?: React.MouseEvent) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Set this add button as selected
    setSelectedElement(`add-${section.variableType}`);

    // Find the highest order index for this variable type
    const sameVariableElements = foundElements.filter(
      (el) => el.section.variableType === section.variableType,
    );
    const maxOrderIndex = Math.max(
      ...sameVariableElements.map((el) => el.orderIndex || 0),
      -1,
    );
    const newOrderIndex = maxOrderIndex + 1;

    const message = `Can you add a new ${section.description} with value: `;

    // Send message to parent window
    window.parent.postMessage(
      {
        type: 'ADD_NEW_VARIABLE',
        variableType: section.variableType,
        description: section.description,
        orderIndex: newOrderIndex,
        message: message,
        replace: true,
      },
      '*',
    );
  };

  if (!isEditMode) return null;

  // Group order-indexed elements to show "add new" buttons
  const orderIndexedGroups = new Map<string, FoundElement[]>();
  foundElements.forEach((foundElement) => {
    if (foundElement.section.isOrderIndexed) {
      const variableType = foundElement.section.variableType;
      if (!orderIndexedGroups.has(variableType)) {
        orderIndexedGroups.set(variableType, []);
      }
      orderIndexedGroups.get(variableType)!.push(foundElement);
    }
  });

  // Calculate selection rectangle for rendering
  const selectionRect =
    selectionStart && selectionEnd
      ? {
          left: Math.min(selectionStart.x, selectionEnd.x),
          top: Math.min(selectionStart.y, selectionEnd.y),
          width: Math.abs(selectionEnd.x - selectionStart.x),
          height: Math.abs(selectionEnd.y - selectionStart.y),
        }
      : null;

  return (
    <div className="fixed inset-0 pointer-events-none z-40">
      {/* Bulk select mode indicator */}
      {isBulkSelectMode && (
        <div
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 pointer-events-auto"
          data-editable-element
        >
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <span className="text-sm font-medium">
              Bulk Select Mode{' '}
              {selectedElements.size > 0 &&
                `(${selectedElements.size} selected)`}
            </span>
            <button
              onClick={exitBulkSelectMode}
              className="bg-gray-500 hover:bg-gray-600 px-2 py-1 rounded text-xs font-medium transition-colors"
            >
              Exit
            </button>
          </div>
        </div>
      )}

      {/* Selection rectangle */}
      {isBulkSelectMode && selectionRect && (
        <div
          className="absolute border-2 border-blue-400 bg-blue-400/20 pointer-events-none z-45"
          style={{
            left: selectionRect.left,
            top: selectionRect.top,
            width: selectionRect.width,
            height: selectionRect.height,
          }}
        />
      )}

      {/* Existing element overlays */}
      {
        foundElements.map((foundElement) => {
          const rect = elementPositions.get(foundElement.uniqueId);

          if (!rect) return null;

          const isHovered = hoveredElement === foundElement.uniqueId;
          const isSelected = selectedElement === foundElement.uniqueId;
          const isBulkSelected = selectedElements.has(foundElement.uniqueId);

          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (!isVisible) return null;

          // For some elements, reduce the overlay area to avoid blocking
          const overlayStyle =
            foundElement.section.id === 'hero-background'
              ? {
                  left: rect.left,
                  top: rect.top + rect.height - Math.min(rect.height, 100),
                  width: Math.min(rect.width, 200),
                  height: Math.min(rect.height, 100),
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
              : foundElement.section.id === 'hero-image' ||
                foundElement.section.id === 'residential-image' ||
                foundElement.section.id === 'commercial-image' ||
                foundElement.section.id === 'rolloff-image' ||
                foundElement.section.id === 'portable-toilet-image'
              ? {
                  left: rect.left + rect.width - Math.min(rect.width, 200),
                  top: rect.top + rect.height - Math.min(rect.height, 150),
                  width: Math.min(rect.width, 200),
                  height: Math.min(rect.height, 150),
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
              : foundElement.section.id === 'facebook-link'
              ? {
                  left: rect.left - 40,
                  top: rect.top - 40,
                  width: rect.width + 40,
                  height: rect.height + 20,
                }
              : foundElement.section.id === 'x-link'
              ? {
                  left: rect.left - 40,
                  top: rect.top + 40,
                  width: rect.width + 40,
                  height: rect.height + 20,
                }
              : {
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                };

          const displayText =
            foundElement.section.isOrderIndexed &&
            foundElement.orderIndex !== undefined
              ? `${foundElement.section.description} #${
                  foundElement.orderIndex + 1
                }`
              : foundElement.section.description;

          return (
            <div
              key={foundElement.uniqueId}
              className="absolute pointer-events-auto transition-all duration-200"
              style={overlayStyle}
              data-editable-element
            >
              {/* Overlay highlight */}
              <div
                className={`absolute inset-0 border-2 rounded cursor-pointer transition-all duration-200 ${
                  isBulkSelected
                    ? 'border-solid border-green-600 bg-green-600/30'
                    : isSelected
                    ? 'border-solid border-blue-600 bg-blue-600/30'
                    : isHovered
                    ? 'border-dashed border-blue-500 bg-blue-500/25'
                    : 'border-dashed border-blue-400 bg-blue-400/10'
                }`}
                onClick={(event) => handleElementClick(foundElement, event)}
                onMouseEnter={() =>
                  !isBulkSelectMode && setHoveredElement(foundElement.uniqueId)
                }
                onMouseLeave={() =>
                  !isBulkSelectMode && setHoveredElement(null)
                }
              />

              {/* Label */}
              {(isHovered || isSelected || isBulkSelected) && (
                <div
                  className={`absolute -translate-x-1/2 -translate-y-1/2 text-white px-2 py-1 rounded text-sm font-medium shadow-lg whitespace-nowrap transition-all duration-200 backdrop-blur-sm cursor-pointer ${
                    isBulkSelected
                      ? 'font-bold scale-105'
                      : isSelected
                      ? 'font-bold scale-105'
                      : isHovered
                      ? 'scale-105'
                      : ''
                  }`}
                  style={{
                    zIndex: 99999,
                    left: '50%',
                    top: '50%',
                    backgroundColor: isBulkSelected
                      ? 'rgba(34, 197, 94, 0.95)'
                      : isSelected
                      ? 'rgba(29, 78, 216, 0.95)'
                      : isHovered
                      ? 'rgba(37, 99, 235, 0.9)'
                      : 'rgba(37, 99, 235, 0.8)',
                  }}
                  onClick={(event) => handleElementClick(foundElement, event)}
                  onMouseEnter={() =>
                    !isBulkSelectMode &&
                    setHoveredElement(foundElement.uniqueId)
                  }
                  onMouseLeave={() =>
                    !isBulkSelectMode && setHoveredElement(null)
                  }
                >
                  {displayText}
                  {isBulkSelected && ' ✓'}
                  {isSelected && !isBulkSelected && ' ✓'}
                </div>
              )}
            </div>
          );
        })
      }

      {
        /* Existing remove buttons and add new buttons - hide in bulk select mode */
      }
      {
        !isBulkSelectMode &&
          foundElements
            .filter((foundElement) => foundElement.section.isOrderIndexed)
            .map((foundElement) => {
              const rect = elementPositions.get(foundElement.uniqueId);
              if (!rect) return null;

              const removeButtonId = `remove-${foundElement.uniqueId}`;
              const isRemoveButtonSelected = selectedElement === removeButtonId;

              return (
                <div
                  key={removeButtonId}
                  className="absolute pointer-events-auto transition-all duration-200"
                  style={{
                    left: rect.left - 40,
                    top: rect.top + rect.height / 2 - 15,
                    width: 30,
                    height: 30,
                    zIndex: 99999,
                  }}
                  data-editable-element
                >
                  <button
                    className={`w-full h-full text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 text-lg font-bold ${
                      isRemoveButtonSelected
                        ? 'bg-red-600 scale-110'
                        : 'bg-red-500 hover:bg-red-600 hover:scale-110'
                    }`}
                    onClick={(event) => handleRemove(foundElement, event)}
                    title={`Remove ${foundElement.section.description} #${
                      (foundElement.orderIndex || 0) + 1
                    }`}
                  >
                    ×
                  </button>
                </div>
              );
            })
      }

      {
        !isBulkSelectMode &&
          Array.from(orderIndexedGroups.entries()).map(
            ([variableType, elements]) => {
              const rect = elementPositions.get(
                elements[elements.length - 1].uniqueId,
              );
              if (!rect) return null;

              const addButtonId = `add-${variableType}`;
              const isAddButtonSelected = selectedElement === addButtonId;

              return (
                <div
                  key={addButtonId}
                  className="absolute pointer-events-auto transition-all duration-200"
                  style={{
                    left: rect.right + 10,
                    top: rect.top + rect.height / 2 - 15,
                    width: 30,
                    height: 30,
                    zIndex: 99999,
                  }}
                  data-editable-element
                >
                  <button
                    className={`w-full h-full text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 text-lg font-bold ${
                      isAddButtonSelected
                        ? 'bg-green-600 scale-110'
                        : 'bg-green-500 hover:bg-green-600 hover:scale-110'
                    }`}
                    onClick={(event) =>
                      handleAddNew(elements[elements.length - 1].section, event)
                    }
                    title={`Add new ${
                      elements[elements.length - 1].section.description
                    }`}
                  >
                    +
                  </button>
                </div>
              );
            },
          )
      }
    </div>
  );
};
